import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, WhistleblowerReport, WhistleblowerStatus } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const ACCS_ROLES = ['ACCS_INVESTIGATOR'];
const STATUSES: WhistleblowerStatus[] = ['SUBMITTED', 'UNDER_REVIEW', 'CONVERTED_TO_CASE', 'DECLINED_INSUFFICIENT', 'DECLINED_OUT_OF_SCOPE', 'CLOSED_NO_ACTION'];

/** GET /api/v1/m40/whistleblower/reports — ACCS triage queue. Never citizen-identity-bearing (see schema.prisma header). */
export function Whistleblower() {
  const { persona } = useAuth();
  const isInvestigator = persona?.roles.includes('ACCS_INVESTIGATOR') ?? false;
  const navigate = useNavigate();
  const [status, setStatus] = useState<WhistleblowerStatus | ''>('');
  const [q, setQ] = useState('');

  const qp = new URLSearchParams({ limit: '50' });
  if (status) qp.set('status', status);
  if (q.trim()) qp.set('q', q.trim());
  const listQ = useApiQuery<Page<WhistleblowerReport>>(isInvestigator ? `/api/v1/m40/whistleblower/reports?${qp.toString()}` : null);

  return (
    <div>
      <PageHeader
        title="Whistleblower Reports"
        subtitle="Anonymous ACCS inbox — no complainant identity is ever stored; only its re-access case key hash."
        actions={
          <Link to="/report-corruption" target="_blank" className="btn-secondary">
            Open public intake form →
          </Link>
        }
      />
      {!isInvestigator ? <RoleNotice roles={ACCS_ROLES} /> : null}

      {isInvestigator ? (
        <>
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <Field label="Ref no. / description contains" className="min-w-[220px] flex-1">
              <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} />
            </Field>
            <Field label="Status" className="!mb-0">
              <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as WhistleblowerStatus | '')}>
                <option value="">All</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No whistleblower reports match" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Ref</th>
                    <th className="table-head">Category</th>
                    <th className="table-head">Subject MDA</th>
                    <th className="table-head">Status</th>
                    <th className="table-head">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((w) => (
                    <tr key={w.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/ombudsman/whistleblower/${w.id}`)}>
                      <td className="table-cell font-mono text-xs">{w.refNo}</td>
                      <td className="table-cell text-xs">{w.category.replaceAll('_', ' ')}</td>
                      <td className="table-cell font-medium">{w.subjectMda ?? '—'}</td>
                      <td className="table-cell">
                        <StatusBadge status={w.status} />
                      </td>
                      <td className="table-cell text-xs">{formatDate(w.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
